// Translated UI strings (snippets/js-variables.liquid). `t` HTML-escapes non-_html keys and
// these land in textContent, so decode once; fall back to English if a key is missing.
const quickOrderString = (() => {
  const decoder = document.createElement('textarea');
  return (key, fallback) => {
    const value = window.FoxTheme?.quickOrderStrings?.[key];
    if (value == null) return fallback;
    decoder.innerHTML = value;
    return decoder.value;
  };
})();

if (!customElements.get('quick-order-component')) {
  customElements.define(
    'quick-order-component',
    class QuickOrderComponent extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.productList = this.querySelector('[data-product-list]');
        this.searchForm = this.querySelector('[data-search-form]');
        this.searchInput = this.querySelector('[data-search-input]');
        this.searchResults = this.querySelector('[data-search-results]');
        this.searchShowMore = this.querySelector('[data-search-show-more]');
        this.uploadInput = this.querySelector('[data-upload-input]');
        this.uploadDropzone = this.querySelector('[data-upload-dropzone]');
        this.uploadFile = this.querySelector('[data-upload-file]');
        this.uploadErrorBanner = this.querySelector('[data-upload-error-banner]');
        this.addToJobListButton = this.querySelector('[data-add-to-job-list]');
        this.addToCartButton = this.querySelector('[data-add-to-cart]');
        this.selectAllCheckbox = this.querySelector('[data-select-all]');

        this.searchForm?.addEventListener('submit', (event) => this.handleSearch(event));
        this.searchInput?.addEventListener('input', () => this.queueSearch());
        this.searchResults?.addEventListener('click', (event) => this.handleSearchResultClick(event));
        this.searchShowMore?.addEventListener('click', () => this.showMoreSearchItems());
        this.querySelector('[data-upload-browse]')?.addEventListener('click', () => this.uploadInput?.click());
        this.uploadInput?.addEventListener('change', () => this.selectUploadFile(this.uploadInput.files?.[0]));
        this.querySelector('[data-upload-submit]')?.addEventListener('click', () =>
          this.handleUpload(this.selectedUploadFile)
        );
        this.querySelector('[data-upload-remove]')?.addEventListener('click', () => this.clearUploadFile());
        this.querySelector('[data-upload-see-details]')?.addEventListener('click', () => this.openUploadErrors());
        this.addToCartButton?.addEventListener('click', () => this.addAllToCart());
        this.addToJobListButton?.addEventListener('click', () =>
          document.querySelector('job-list-add-form')?.open(this.getSelectedItems(), this.addToJobListButton)
        );
        this.productList?.addEventListener('click', (event) => this.handleListClick(event));
        this.productList?.addEventListener('change', () => this.updateSummary());
        this.selectAllCheckbox?.addEventListener('change', () => this.toggleSelectAll());
        document.querySelectorAll('[data-upload-error-close]').forEach((button) => {
          button.addEventListener('click', () => this.closeUploadErrors());
        });
        document.querySelector('[data-upload-again]')?.addEventListener('click', () => {
          this.closeUploadErrors();
          this.uploadInput?.click();
        });

        // Fired by the "Add Item to Job List" modal (assets/job-lists.js).
        document.addEventListener('job-list:items-added', () =>
          this.showOrderNotification(window.FoxTheme?.jobListsStrings?.itemsAdded ?? 'Items added to job list.')
        );

        this.setupDropzone();
        this.updateSummary();
      }

      setupDropzone() {
        if (!this.uploadDropzone) return;

        for (const eventName of ['dragenter', 'dragover']) {
          this.uploadDropzone.addEventListener(eventName, (event) => {
            event.preventDefault();
            this.uploadDropzone.classList.add('is-dragging');
          });
        }

        for (const eventName of ['dragleave', 'drop']) {
          this.uploadDropzone.addEventListener(eventName, (event) => {
            event.preventDefault();
            this.uploadDropzone.classList.remove('is-dragging');
          });
        }

        this.uploadDropzone.addEventListener('drop', (event) => {
          const file = event.dataTransfer?.files?.[0];
          if (file) this.selectUploadFile(file);
        });
      }

      selectUploadFile(file) {
        if (!file) return;
        this.selectedUploadFile = file;
        const fileName = this.querySelector('[data-upload-file-name]');
        const fileSize = this.querySelector('[data-upload-file-size]');
        if (fileName) fileName.textContent = file.name;
        if (fileSize) fileSize.textContent = this.formatFileSize(file.size);
        this.uploadFile?.toggleAttribute('hidden', false);
        this.uploadErrorBanner?.toggleAttribute('hidden', true);
        this.setMessage(this.querySelector('[data-upload-message]'), '');
      }

      clearUploadFile() {
        this.selectedUploadFile = null;
        if (this.uploadInput) this.uploadInput.value = '';
        this.uploadFile?.toggleAttribute('hidden', true);
        this.uploadErrorBanner?.toggleAttribute('hidden', true);
        this.setMessage(this.querySelector('[data-upload-message]'), '');
      }

      formatFileSize(bytes = 0) {
        if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
        const megabytes = bytes / (1024 * 1024);
        return `${megabytes >= 10 ? Math.round(megabytes) : megabytes.toFixed(1)} MB`;
      }

      async handleSearch(event) {
        event.preventDefault();
        const term = this.searchInput?.value.trim();
        if (!term) return;

        await this.showSearchResults(term);
      }

      queueSearch() {
        window.clearTimeout(this.searchTimer);
        const term = this.searchInput?.value.trim() || '';

        if (term.length < 2) {
          this.searchAbortController?.abort();
          this.searchResults?.replaceChildren();
          this.searchResults?.toggleAttribute('hidden', true);
          if (this.searchShowMore) this.searchShowMore.hidden = true;
          this.setMessage(this.querySelector('[data-search-message]'), '');
          return;
        }

        this.searchTimer = window.setTimeout(() => this.showSearchResults(term), 250);
      }

      async showSearchResults(term) {
        const message = this.querySelector('[data-search-message]');
        this.setMessage(message, quickOrderString('searching', 'Searching…'));
        this.setBusy(this.searchForm, true);
        this.searchAbortController?.abort();
        this.searchAbortController = new AbortController();

        try {
          const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
          url.searchParams.set('q', term);
          url.searchParams.set('type', 'product');
          url.searchParams.set('section_id', this.dataset.lookupSection || 'quick-order-lookup');

          const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: this.searchAbortController.signal,
          });
          if (!response.ok) throw new Error('Search request failed');

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const items = Array.from(documentFragment.querySelectorAll('quick-order-item')).slice(0, 6);

          if (!items.length) {
            this.searchResults.replaceChildren();
            this.searchResults.hidden = true;
            if (this.searchShowMore) this.searchShowMore.hidden = true;
            this.setMessage(message, quickOrderString('noResults', 'No item found for “[term]”.').replace('[term]', term), true);
            return;
          }

          this.renderSearchItems(items, {}, this.searchResults, term);
          this.setMessage(message, '');
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('[quick-order] Search failed', error);
          this.setMessage(message, quickOrderString('searchError', 'Unable to search right now. Please try again.'), true);
        } finally {
          this.setBusy(this.searchForm, false);
        }
      }

      handleSearchResultClick(event) {
        const button = event.target.closest('[data-add-search-result]');
        if (!button) return;

        const result = button.closest('quick-order-item');
        if (!result) return;

        const quantity = Math.max(1, Number(result.querySelector('[data-item-quantity]')?.value) || 1);
        const item = result.cloneNode(true);
        item.classList.remove('quick-order-search-result');
        item.querySelector('[data-add-search-result]')?.remove();
        item.querySelectorAll('mark').forEach((mark) => mark.replaceWith(document.createTextNode(mark.textContent || '')));
        const itemCheckbox = item.querySelector('[data-item-checkbox]');
        if (itemCheckbox) itemCheckbox.checked = true;
        this.mergeItems([item], { [this.normalizeSku(item.dataset.sku)]: quantity });
        this.showOrderNotification(quickOrderString('itemsAdded', 'All items added successfully.'));
      }

      renderSearchItems(items, quantitiesBySku = {}, target = this.searchResults, searchTerm = '') {
        if (!target) return;

        target.replaceChildren();
        items.forEach((item) => {
          const sku = this.normalizeSku(item.dataset.sku);
          const quantity = Math.max(1, Number(quantitiesBySku[sku]) || 1);
          const quantityInput = item.querySelector('[data-item-quantity]');
          if (quantityInput) quantityInput.value = String(quantity);

          item.classList.add('quick-order-search-result');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'btn btn--primary quick-order-search-result__add';
          button.setAttribute('data-add-search-result', '');
          button.innerHTML = '<span class="btn__text"></span>';
          button.firstChild.textContent = this.dataset.addLabel || quickOrderString('addToOrder', 'Add to Order');
          item.appendChild(button);
          this.highlightSearchMatches(item, searchTerm);
          target.appendChild(item);
        });

        target.hidden = items.length === 0;
        target.dataset.visibleLimit = '4';
        this.updateSearchShowMore(target);
      }

      highlightSearchMatches(item, searchTerm) {
        const term = searchTerm.trim();
        if (!term) return;

        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const matcher = new RegExp(`(${escapedTerm})`, 'gi');
        const targets = item.querySelectorAll(
          '.quick-order-item__title, .quick-order-item__sku, .quick-order-item__upc'
        );

        targets.forEach((target) => {
          const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
          const textNodes = [];
          while (walker.nextNode()) textNodes.push(walker.currentNode);

          textNodes.forEach((textNode) => {
            const text = textNode.textContent || '';
            if (!matcher.test(text)) {
              matcher.lastIndex = 0;
              return;
            }

            matcher.lastIndex = 0;
            const fragment = document.createDocumentFragment();
            text.split(matcher).forEach((part) => {
              if (!part) return;
              if (part.toLowerCase() === term.toLowerCase()) {
                const mark = document.createElement('mark');
                mark.textContent = part;
                fragment.appendChild(mark);
              } else {
                fragment.appendChild(document.createTextNode(part));
              }
            });
            textNode.replaceWith(fragment);
          });
        });
      }

      updateSearchShowMore(target) {
        if (!target || !this.searchShowMore) return;

        const items = Array.from(target.querySelectorAll('quick-order-item')).filter((item) => !item.hidden);
        const limit = Math.max(4, Number(target.dataset.visibleLimit) || 4);
        items.forEach((item, index) => item.classList.toggle('is-result-collapsed', index >= limit));

        this.searchShowMoreTarget = target;
        this.searchShowMore.hidden = items.length <= limit;
      }

      showMoreSearchItems() {
        const target = this.searchShowMoreTarget;
        if (!target) return;
        target.dataset.visibleLimit = String((Number(target.dataset.visibleLimit) || 4) + 4);
        this.updateSearchShowMore(target);
      }

      async handleUpload(file) {
        const message = this.querySelector('[data-upload-message]');
        if (!file) return;
        const submitButton = this.querySelector('[data-upload-submit]');
        this.uploadErrorBanner?.toggleAttribute('hidden', true);

        if (!file.name.toLowerCase().endsWith('.csv')) {
          this.displayUploadErrors([
            {
              row: '—',
              sku: file.name,
              quantity: '—',
              reason: quickOrderString('unsupportedFormat', 'Unsupported file format'),
            },
          ]);
          return;
        }

        this.setMessage(message, quickOrderString('reading', 'Reading [file]…').replace('[file]', file.name));
        this.setBusy(submitButton, true);

        try {
          const text = await file.text();
          const entries = this.parseCsv(text);
          if (!entries.length) {
            this.displayUploadErrors([
              {
                row: '—',
                sku: file.name,
                quantity: '—',
                reason: quickOrderString('noValidRows', 'No valid SKU rows found'),
              },
            ]);
            return;
          }

          const results = await Promise.all(entries.map(({ sku }) => this.lookupItem(sku)));
          const items = [];
          const importedVariantIds = new Set();
          const quantities = {};
          const errors = [];

          results.forEach((item, index) => {
            const entry = entries[index];
            if (!item) {
              errors.push({ ...entry, reason: quickOrderString('productNotFound', 'Product not found') });
              return;
            }
            if (item.dataset.available !== 'true') {
              errors.push({ ...entry, reason: quickOrderString('outOfStock', 'Out of Stock'), outOfStock: true });
              return;
            }
            const itemSku = this.normalizeSku(item.dataset.sku || entry.sku);
            quantities[itemSku] = (quantities[itemSku] || 0) + entry.quantity;
            if (!importedVariantIds.has(item.dataset.variantId)) {
              importedVariantIds.add(item.dataset.variantId);
              items.push(item);
            }
          });

          this.mergeItems(items, quantities);
          if (items.length) this.showOrderNotification(quickOrderString('itemsAdded', 'All items added successfully.'));
          this.setMessage(message, '');
          if (errors.length) {
            this.displayUploadErrors(errors);
          }
        } catch (error) {
          console.error('[quick-order] CSV import failed', error);
          this.displayUploadErrors([
            {
              row: '—',
              sku: file.name,
              quantity: '—',
              reason: quickOrderString('unreadableFile', 'Unable to read file'),
            },
          ]);
        } finally {
          this.setBusy(submitButton, false);
        }
      }

      async lookupItem(term) {
        const lookups = [`variants.sku:${term}`, `variants.barcode:${term}`, term];

        for (const query of lookups) {
          const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('type', 'product');
          url.searchParams.set('section_id', this.dataset.lookupSection || 'quick-order-lookup');

          const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });

          if (!response.ok) continue;
          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const item = documentFragment.querySelector('quick-order-item');
          if (item) return item;
        }

        return null;
      }

      mergeItems(items, quantitiesBySku = {}) {
        for (const item of items) {
          const variantId = item.dataset.variantId;
          const sku = this.normalizeSku(item.dataset.sku);
          if (!variantId) continue;

          const existing = this.productList.querySelector(`quick-order-item[data-variant-id="${CSS.escape(variantId)}"]`);
          const requestedQuantity = Math.max(1, Number(quantitiesBySku[sku]) || 1);

          if (existing) {
            const input = existing.querySelector('[data-item-quantity]');
            input.value = String(Math.max(1, Number(input.value) || 1) + requestedQuantity);
            const checkbox = existing.querySelector('[data-item-checkbox]');
            if (checkbox) checkbox.checked = true;
          } else {
            const input = item.querySelector('[data-item-quantity]');
            if (input) input.value = String(requestedQuantity);
            const checkbox = item.querySelector('[data-item-checkbox]');
            if (checkbox) checkbox.checked = true;
            this.productList.appendChild(item);
          }
        }

        this.updateSummary();
      }

      handleListClick(event) {
        const removeButton = event.target.closest('[data-remove-item]');
        if (!removeButton) return;
        removeButton.closest('quick-order-item')?.remove();
        this.updateSummary();
      }

      toggleSelectAll() {
        const checked = Boolean(this.selectAllCheckbox?.checked);
        this.productList?.querySelectorAll('[data-item-checkbox]').forEach((checkbox) => {
          checkbox.checked = checked;
        });
        this.updateSummary();
      }

      updateSelectionState() {
        if (!this.selectAllCheckbox) return;

        const checkboxes = Array.from(this.productList?.querySelectorAll('[data-item-checkbox]') || []);
        const checkedCount = checkboxes.filter((checkbox) => checkbox.checked).length;

        this.selectAllCheckbox.disabled = checkboxes.length === 0;
        this.selectAllCheckbox.checked = checkboxes.length > 0 && checkedCount === checkboxes.length;
        this.selectAllCheckbox.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length;
      }

      updateSummary() {
        const items = Array.from(this.productList?.querySelectorAll('quick-order-item') || []);
        let orderTotal = 0;

        for (const item of items) {
          const input = item.querySelector('[data-item-quantity]');
          const itemQuantity = Math.max(1, Number(input?.value) || 1);
          const itemTotal = itemQuantity * (Number(item.dataset.unitPrice) || 0);
          if (item.querySelector('[data-item-checkbox]')?.checked) {
            orderTotal += itemTotal;
          }

          const totalElement = item.querySelector('[data-item-total]');
          if (totalElement) totalElement.textContent = this.formatMoney(itemTotal);
        }

        this.querySelector('[data-product-count]').textContent = String(items.length);
        const orderTotalElement = this.querySelector('[data-order-total]');
        if (orderTotalElement) orderTotalElement.textContent = this.formatMoney(orderTotal);
        const orderTotalRow = this.querySelector('[data-order-total-row]');
        if (orderTotalRow) orderTotalRow.hidden = items.length === 0;

        this.querySelector('[data-empty-state]')?.toggleAttribute('hidden', items.length > 0);
        const hasSelectedItems = items.some((item) => item.querySelector('[data-item-checkbox]')?.checked);
        if (this.addToJobListButton) this.addToJobListButton.disabled = !hasSelectedItems;
        if (this.addToCartButton) this.addToCartButton.disabled = orderTotal <= 0;
        this.updateSelectionState();
        // Lets a host (e.g. the Job List detail view) persist the rows after every edit.
        this.dispatchEvent(new CustomEvent('quick-order:change', { bubbles: true }));
      }

      getSelectedItems() {
        return Array.from(this.productList.querySelectorAll('quick-order-item')).filter(
          (item) => item.querySelector('[data-item-checkbox]')?.checked
        );
      }

      async addAllToCart() {
        const items = this.getSelectedItems()
          .map((item) => ({
            id: Number(item.dataset.variantId),
            quantity: Math.max(1, Number(item.querySelector('[data-item-quantity]')?.value) || 1),
          }));
        if (!items.length) return;

        const status = this.querySelector('[data-cart-status]');
        this.setMessage(status, '');
        this.setBusy(this.addToCartButton, true);

        try {
          const sections = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', {
              bubbles: true,
              detail: { sections },
            })
          );

          const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname,
            }),
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Cart update failed');

          const cartResponse = await fetch(this.dataset.cartUrl || '/cart.js', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          const cart = await cartResponse.json();
          cart.sections = result.sections;

          if (window.FoxTheme?.pubsub) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-order',
              cart,
            });
          }

          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.show === 'function') {
            cartDrawer.show(this.addToCartButton);
          } else {
            document.dispatchEvent(
              new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { open: true },
              })
            );
          }
          this.setMessage(status, '');
        } catch (error) {
          console.error('[quick-order] Add to cart failed', error);
          this.setMessage(status, error.message || quickOrderString('addToCartError', 'Unable to add items to cart.'), true);
        } finally {
          this.setBusy(this.addToCartButton, false);
        }
      }

      parseCsv(text = '') {
        const rows = this.parseCsvRows(text);
        if (!rows.length) return [];

        const forceHeader = this.querySelector('[data-upload-has-heading]')?.checked;
        const firstRow = rows[0].map((cell) => cell.toLowerCase());
        let skuIndex = firstRow.findIndex((cell) => cell.includes('sku'));
        let quantityIndex = firstRow.findIndex((cell) => cell.includes('quantity') || cell.includes('qty'));
        const hasHeader = forceHeader || skuIndex >= 0;

        if (skuIndex < 0) {
          skuIndex = 0;
          quantityIndex = rows[0].length > 1 ? 1 : -1;
        }

        const startIndex = hasHeader ? 1 : 0;
        const entries = [];

        rows.slice(startIndex, 501 + startIndex).forEach((row, index) => {
          const sku = String(row[skuIndex] || '').trim();
          if (!sku) return;
          const quantityValue = quantityIndex >= 0 ? Number(row[quantityIndex]) : 1;
          entries.push({
            row: index + startIndex + 1,
            sku,
            quantity: Math.max(1, Math.floor(quantityValue) || 1),
          });
        });

        return entries;
      }

      parseCsvRows(text) {
        const rows = [];
        let row = [];
        let value = '';
        let quoted = false;

        for (let index = 0; index < text.length; index += 1) {
          const character = text[index];
          const nextCharacter = text[index + 1];

          if (character === '"' && quoted && nextCharacter === '"') {
            value += '"';
            index += 1;
          } else if (character === '"') {
            quoted = !quoted;
          } else if (character === ',' && !quoted) {
            row.push(value.trim());
            value = '';
          } else if ((character === '\n' || character === '\r') && !quoted) {
            if (character === '\r' && nextCharacter === '\n') index += 1;
            row.push(value.trim());
            if (row.some(Boolean)) rows.push(row);
            row = [];
            value = '';
          } else {
            value += character;
          }
        }

        row.push(value.trim());
        if (row.some(Boolean)) rows.push(row);
        return rows;
      }

      normalizeSku(value) {
        return String(value || '').trim().toLowerCase();
      }

      formatMoney(cents) {
        const currency = window.Shopify?.currency?.active || 'USD';
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency,
        }).format((Number(cents) || 0) / 100);
      }

      setBusy(element, busy) {
        if (!element) return;
        const button = element.matches?.('button') ? element : element.querySelector?.('button[type="submit"]');
        if (button) button.disabled = busy;
        if (button) button.classList.toggle('btn--loading', busy);
        element.toggleAttribute?.('aria-busy', busy);
        element.classList?.toggle('is-loading', busy);
      }

      setMessage(element, message, isError = false) {
        if (!element) return;
        element.textContent = message;
        element.classList.toggle('is-error', isError);
      }

      showOrderNotification(message, isWarning = false) {
        const notification = this.querySelector('[data-order-notification]');
        const text = notification?.querySelector('[data-order-notification-text]');
        if (!notification || !text) return;

        text.textContent = message;
        notification.classList.toggle('is-warning', isWarning);
        notification.hidden = false;
        window.clearTimeout(this.notificationTimer);
        this.notificationTimer = window.setTimeout(() => {
          notification.hidden = true;
        }, 3000);
      }

      showUploadErrors(errors, shouldOpen = true) {
        const dialog = document.querySelector('[data-upload-error-dialog]');
        const tableBody = dialog?.querySelector('[data-upload-error-rows]');
        if (!dialog || !tableBody) return;

        tableBody.replaceChildren();
        errors.forEach((error) => {
          const row = document.createElement('tr');
          const labels = [
            quickOrderString('row', 'Row #'),
            quickOrderString('sku', 'SKU'),
            quickOrderString('quantity', 'Quantity'),
            quickOrderString('reason', 'Reason'),
          ];
          [error.row, error.sku, error.quantity, error.reason].forEach((value, index) => {
            const cell = document.createElement('td');
            const cellValue = document.createElement('span');
            cell.dataset.label = labels[index];
            cellValue.className = 'quick-order-error-modal__value';
            cellValue.textContent = String(value ?? '');
            cell.appendChild(cellValue);
            if (index === 3) {
              cell.className = 'quick-order-error-modal__reason';
              if (error.outOfStock) {
                cell.classList.add('quick-order-error-modal__reason--stock');
              }
            }
            row.appendChild(cell);
          });
          tableBody.appendChild(row);
        });

        if (shouldOpen) this.openUploadErrors();
      }

      displayUploadErrors(errors) {
        this.showUploadErrors(errors, false);
        this.uploadErrorBanner?.toggleAttribute('hidden', false);
        this.setMessage(this.querySelector('[data-upload-message]'), '');
      }

      openUploadErrors() {
        const dialog = document.querySelector('[data-upload-error-dialog]');
        if (!dialog || dialog.open) return;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }

      closeUploadErrors() {
        const dialog = document.querySelector('[data-upload-error-dialog]');
        if (!dialog) return;
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
    }
  );
}
